import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import { alpha } from '@mui/material/styles';

const WhyLenz = () => {
	return (
		<Container
			maxWidth="lg"
			sx={{
				py: { xs: 6, sm: 8 },
			}}
		>
			<Typography
				variant="h2"
				align="center"
				sx={{
					fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
					fontWeight: 500,
					mb: { xs: 4, sm: 6 },
				}}
			>
				Miksi valita Lenz Platform?
			</Typography>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 4,
				}}
			>
				{/* Hinta ja joustavuus & Arvolupaus */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
						gap: 4,
					}}
				>
					{/* Hinta ja joustavuus */}
					<Box
						sx={{
							borderRadius: 2,
							border: '2px solid',
							borderColor: alpha('#7C55E5', 0.2),
							p: { xs: 3, sm: 4 },
							position: 'relative',
							'&::after': {
								content: '""',
								position: 'absolute',
								top: 0,
								right: -2,
								bottom: -2,
								left: 0,
								background: alpha('#7C55E5', 0.1),
								borderRadius: 2,
								zIndex: -1,
							},
						}}
					>
						<Typography
							variant="h3"
							sx={{
								fontSize: { xs: '1.5rem', sm: '2rem' },
								color: 'primary.main',
								mb: { xs: 2, sm: 3 },
							}}
						>
							Hinta ja joustavuus
						</Typography>

						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
							}}
						>
							<Typography
								variant="body1"
								sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
							>
								<strong>Kiinteä hinta:</strong> 40 €/h + alv,
								ilman piilokuluja
							</Typography>
							<Typography
								variant="body1"
								sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
							>
								<strong>Ei pitkiä sitoumuksia</strong> – maksat
								vain tehdystä työstä
							</Typography>
							<Typography
								variant="body1"
								sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
							>
								<strong>Nopea saatavuus</strong> – sopiva tekijä
								löytyy minuuteissa
							</Typography>
							<Typography
								variant="body1"
								sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
							>
								<strong>Ei sitoutumispakkoa</strong> – voit
								lopettaa yhteistyön milloin tahansa.
							</Typography>
						</Box>
					</Box>

					{/* Arvolupaus */}
					<Box
						sx={{
							borderRadius: 2,
							border: '2px solid',
							borderColor: alpha('#7C55E5', 0.2),
							p: { xs: 3, sm: 4 },
							position: 'relative',
							'&::after': {
								content: '""',
								position: 'absolute',
								top: 0,
								right: -2,
								bottom: -2,
								left: 0,
								background: alpha('#7C55E5', 0.1),
								borderRadius: 2,
								zIndex: -1,
							},
						}}
					>
						<Typography
							variant="h3"
							sx={{
								fontSize: { xs: '1.5rem', sm: '2rem' },
								color: 'primary.main',
								mb: { xs: 2, sm: 3 },
							}}
						>
							Arvolupauksemme
						</Typography>

						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 3,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									gap: 2,
									alignItems: 'flex-start',
								}}
							>
								<CheckCircleIcon
									sx={{ color: 'primary.main', mt: 0.5 }}
								/>
								<Box>
									<Typography
										variant="h6"
										sx={{
											mb: 0.5,
											fontSize: {
												xs: '1.1rem',
												sm: '1.25rem',
											},
										}}
									>
										Nopeus ja tehokkuus
									</Typography>
									<Typography variant="body1">
										Määrittele tarpeesi ja valitse sopiva
										tekijä alle 10 minuutissa.
									</Typography>
								</Box>
							</Box>

							<Box
								sx={{
									display: 'flex',
									gap: 2,
									alignItems: 'flex-start',
								}}
							>
								<CheckCircleIcon
									sx={{ color: 'primary.main', mt: 0.5 }}
								/>
								<Box>
									<Typography
										variant="h6"
										sx={{
											mb: 0.5,
											fontSize: {
												xs: '1.1rem',
												sm: '1.25rem',
											},
										}}
									>
										Laadukkaat tekijät
									</Typography>
									<Typography variant="body1">
										Validoidut ja motivoituneet talentit.
									</Typography>
								</Box>
							</Box>

							<Box
								sx={{
									display: 'flex',
									gap: 2,
									alignItems: 'flex-start',
								}}
							>
								<CheckCircleIcon
									sx={{ color: 'primary.main', mt: 0.5 }}
								/>
								<Box>
									<Typography
										variant="h6"
										sx={{
											mb: 0.5,
											fontSize: {
												xs: '1.1rem',
												sm: '1.25rem',
											},
										}}
									>
										Joustavuus
									</Typography>
									<Typography variant="body1">
										Ei sitoutumispakkoa, maksat vain
										tehdystä työstä.
									</Typography>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>

				{/* Visio */}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
						gap: { xs: 2, md: 4 },
						borderRadius: 2,
						border: '2px solid',
						borderColor: alpha('#7C55E5', 0.2),
						p: { xs: 3, sm: 4 },
						position: 'relative',
						'&::after': {
							content: '""',
							position: 'absolute',
							top: 0,
							right: -2,
							bottom: -2,
							left: 0,
							background: alpha('#7C55E5', 0.1),
							borderRadius: 2,
							zIndex: -1,
						},
					}}
				>
					<Box
						component="img"
						src="/images/platform_landing/people-connected-social-media.jpg"
						alt="People connected"
						sx={{
							width: '100%',
							height: { xs: '200px', sm: '300px', md: '350px' },
							objectFit: 'cover',
							borderRadius: 2,
						}}
					/>
					<Box>
						<Typography
							variant="h3"
							sx={{
								fontSize: { xs: '1.5rem', sm: '2rem' },
								color: 'primary.main',
								mb: { xs: 2, sm: 3 },
							}}
						>
							Visiomme
						</Typography>

						<Typography
							variant="body1"
							sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
						>
							<strong>
								Haluamme rakentaa työelämää, jossa potentiaali
								on yhtä arvokasta kuin kokemus
							</strong>
							, ja missä päätöksiä tehdään enemmän tulevaisuudesta
							innostuneena, kuin historiasta oppineena.
						</Typography>
					</Box>
				</Box>

				{/* Laatutakuu */}
				<Box
					sx={{
						borderRadius: 2,
						border: '2px solid',
						borderColor: alpha('#7C55E5', 0.2),
						p: { xs: 3, sm: 4 },
						position: 'relative',
						'&::after': {
							content: '""',
							position: 'absolute',
							top: 0,
							right: -2,
							bottom: -2,
							left: 0,
							background: alpha('#7C55E5', 0.1),
							borderRadius: 2,
							zIndex: -1,
						},
					}}
				>
					<Typography
						variant="h3"
						sx={{
							fontSize: { xs: '1.5rem', sm: '2rem' },
							color: 'primary.main',
							mb: { xs: 2, sm: 3 },
						}}
					>
						Laatutakuu
					</Typography>

					<Typography
						variant="body1"
						sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 3 }}
					>
						Kaikki alustamme kautta työskentelevät talentit ovat
						validoituneet rekrytointiprosessimme kautta
						talentpooliimme. Haluamme tarjota asiakkaillemme
						huolettoman, joustavan ja luotettavan palvelukokemuksen,
						joten tarjoamme vain parhaimpia tulevaisuuden osaajia
						palvelumme kautta.
					</Typography>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								gap: 2,
								alignItems: 'flex-start',
							}}
						>
							<Box
								sx={{
									position: 'relative',
									width: 24,
									height: 24,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<CircleIcon
									sx={{ color: 'primary.main', fontSize: 24 }}
								/>
								<Typography
									sx={{
										position: 'absolute',
										color: 'white',
										fontSize: '0.875rem',
										fontWeight: 'bold',
									}}
								>
									1
								</Typography>
							</Box>
							<Typography
								variant="body1"
								sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
							>
								Mikäli olette mistään tahansa syystä tyytymätön
								ensisijaiseen valintaanne, voitte ilman
								suurempia perusteluja vaihtaa toiseen tekijään
								platformin kautta
							</Typography>
						</Box>

						<Box
							sx={{
								display: 'flex',
								gap: 2,
								alignItems: 'flex-start',
							}}
						>
							<Box
								sx={{
									position: 'relative',
									width: 24,
									height: 24,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<CircleIcon
									sx={{ color: 'primary.main', fontSize: 24 }}
								/>
								<Typography
									sx={{
										position: 'absolute',
										color: 'white',
										fontSize: '0.875rem',
										fontWeight: 'bold',
									}}
								>
									2
								</Typography>
							</Box>
							<Typography
								variant="body1"
								sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
							>
								Mikäli olette edelleen tyytymättömiä laatuun,
								niin tarjoamme verkostomme kautta kokeneemman
								konsultin samalla hinnalla varmistamaan
								laadukkaan lopputuloksen
							</Typography>
						</Box>
					</Box>
				</Box>
			</Box>
		</Container>
	);
};

export default WhyLenz;
